import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Stack,
  Button,
  Fab,
  useTheme,
  Divider,
  Skeleton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ChatIcon from "@mui/icons-material/Chat";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import {EditNote, TextFields} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import {useSnackbar} from "notistack";

const AdDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("user"));
  const [messageToSend,setMessageToSend] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const {enqueueSnackbar} = useSnackbar();


  // Slider settings
  const hasMultiple = ad?.ad_photos.length > 1;
  const sliderSettings = {
    dots: hasMultiple,
    infinite: hasMultiple,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
  };

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const { response, message } = await Helper.Get({
          url: api_Routes.UserAds.GetOne + id,
          hasToken: true,
        });
        if (response) {
          setAd(response?.data);
        }
      } catch (error) {
        console.error("Error fetching ad details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAd();
  }, [id]);

  const handleStartChat = () => {
    navigate(`/chat/${ad?.user_id}`);
    console.log("Start chat with:", ad?.user?.id);
  };
  const handleSendMessage = async () => {
    if (!messageToSend) {
      enqueueSnackbar("Please enter a message to sent", {variant: "error"});
      return;
    }
    try {
      setMessageLoading(true)
      const { response, message } = await Helper.Post({
        url: `${api_Routes.UserChat.SendDirectMessage}?&body=${messageToSend}&ad_id=${id}`,
        hasToken: true,
      });

      if (!response) throw new Error("Failed to send message");

      if(response?.success){
        setMessageSent(true)
      }
      setMessageToSend("");

    } catch (error) {

      console.error("Error sending message:", error);
    } finally {
      setMessageLoading(false);
    }
  };


  const handleEditAd = () => {
    navigate(`/Edit-ad/${ad?.id}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Skeleton
          variant="rectangular"
          width={200}
          height={40}
          sx={{ mb: 2 }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={500} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={100} />
            <Skeleton variant="text" height={40} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!ad) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h4">Ad not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          mb: 2,
          backgroundColor: theme.palette.background.paper,
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <ArrowBack />
      </IconButton>

      {/* Floating Chat Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleStartChat}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          zIndex: 1000,
          boxShadow: 3,
        }}
      >
        <ChatIcon />
      </Fab>

      <Grid container spacing={4}>
        {/* Photo Slider Column */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            {ad.ad_photos?.length > 0 ? (
              <Slider {...sliderSettings}>
                {ad.ad_photos.map((photo, index) => (
                  <div key={index}>
                    <CardMedia
                      component="img"
                      height="500"
                      image={photo.url}
                      alt={`Ad image ${index + 1}`}
                      sx={{
                        objectFit: "contain",
                        borderRadius: 2,
                        maxHeight: "70vh",
                      }}
                    />
                  </div>
                ))}
              </Slider>
            ) : (
              <CardMedia
                component="img"
                height="500"
                image="/placeholder-image.jpg"
                alt="No images available"
                sx={{
                  objectFit: "cover",
                  borderRadius: 2,
                  maxHeight: "70vh",
                }}
              />
            )}
          </Card>
        </Grid>

        {/* Details Column */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack spacing={3}>
                {/* Title and Category */}
                <div>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.2,
                    }}
                  >
                    {ad.title}
                  </Typography>
                  <Chip
                    label={
                      ad.ad_sub_category_type?.ad_sub_category?.ad_categories
                        ?.name
                    }
                    color="secondary"
                    sx={{ fontWeight: 600 }}
                  />
                </div>

                {/* Price Section */}
                {ad.is_show_price ? (
                  <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontWeight: 800 }}
                  >
                    ${ad.price.toLocaleString()}
                  </Typography>
                ) : (
                  <></>
                )}

                {/* Description */}
                {ad.description && (
                  <div>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Description
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {ad.description}
                    </Typography>
                  </div>
                )}

                <Divider />

                {/* Contact Section */}
                <div style={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    {ad.user_id != user.user_id ?  "Contact Seller" : " Edit Ad"}
                  </Typography>

                  <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ maxWidth: 400, margin: "0 auto" }}
                  >
                    <Grid container spacing={2} justifyContent="center">
                      {
                        ad.user_id != user.user_id ?
                            <>
                              {/* Phone and Email */}
                              {ad.phone && (
                                  <Grid item xs={12} sm={6}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<span>📞</span>}
                                        href={`tel:${ad.phone}`}
                                        sx={{
                                          textTransform: "none",
                                          px: 3,
                                          minWidth: 150,
                                        }}
                                    >
                                      Call Now
                                    </Button>
                                  </Grid>
                              )}
                              {ad.email && (
                                  <Grid item xs={12} sm={6}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<span>✉️</span>}
                                        href={`mailto:${ad.email}`}
                                        sx={{
                                          textTransform: "none",
                                          px: 3,
                                          minWidth: 150,
                                        }}
                                    >
                                      Send Email
                                    </Button>
                                  </Grid>
                              )}
                              {
                                !messageSent ?
                                    <>
                                      {/* Chat Button - Full width but centered */}
                                      <Grid
                                          item
                                          xs={12}
                                          sx={{ display: "flex", justifyContent: "center" }}
                                      >
                                        <TextField
                                            style={{width:"100%"}}
                                            value={messageToSend}
                                            required={true}
                                            label="message"
                                            helperText={"Send direct message to Advertisement" }
                                            onChange={(e) => {
                                              setMessageToSend(e.target.value);

                                            }}
                                        />
                                      </Grid>
                                      <Grid
                                          item
                                          xs={12}
                                          sx={{ display: "flex", justifyContent: "center" }}
                                      >


                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            endIcon={<SendIcon />}
                                            onClick={handleSendMessage}
                                            disabled={messageLoading}
                                            sx={{
                                              textTransform: "none",
                                              py: 1.5,
                                              px: 4,
                                              fontWeight: 600,
                                              width: "100%",
                                              maxWidth: 300,
                                            }}
                                        >
                                          {messageLoading ? (
                                              <CircularProgress size={22} sx={{color: "black"}}/>
                                          ) : (
                                              "Send"
                                          )}
                                        </Button>
                                      </Grid>
                                    </> :
                                    <Typography>Message sent successfully</Typography>
                              }

                            </>
                            :
                            <Grid
                                item
                                xs={12}
                                sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<EditNote />}
                                  onClick={handleEditAd}
                                  sx={{
                                    textTransform: "none",
                                    py: 1.5,
                                    px: 4,
                                    fontWeight: 600,
                                    width: "100%",
                                    maxWidth: 300,
                                  }}
                              >
                                Edit
                              </Button>
                            </Grid>
                      }

                    </Grid>
                  </Stack>
                </div>

                <Divider />

                {/* Additional Details */}
                <div>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Subcategory
                      </Typography>
                      <Typography variant="body1">
                        {ad.ad_sub_category_type?.ad_sub_category?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Subcategory type
                      </Typography>
                      <Typography variant="body1">
                        {ad.ad_sub_category_type?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Posted
                      </Typography>
                      <Typography variant="body1">
                        {new Date(ad.created_at).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        address
                      </Typography>
                      <Typography variant="body1">{ad.address}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Website:{" "}
                      </Typography>
                      <a
                        href={ad?.site_url}
                        className="text-primary hover:underline ml-2"
                      >
                        {ad?.site_url}
                      </a>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Video:{" "}
                      </Typography>
                      {ad?.video_url && (
                        <a
                          href={ad.video_url}
                          className="text-primary hover:underline ml-2"
                        >
                          Watch Video
                        </a>
                      )}
                    </Grid>
                  </Grid>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdDetailsPage;
